import { useEffect, useRef } from 'react';

/**
 * useSectionSortable — native HTML5 drag-and-drop reorder for sortable items
 * inside a section (buttons, gallery images, etc.).
 *
 * Listeners are bound to `document` (capture phase) so we don't race with
 * canvasRef.current population — the existing useInlineButtonEditor uses
 * this same pattern. We scope by walking up to the nearest
 * [data-testid^="live-block-"] wrapper inside the canvas; cross-section
 * drags are a no-op.
 *
 * Conventions:
 *   • Buttons: `data-wb-button="1"` + `data-wb-button-index="<i>"`
 *   • Gallery images: `data-wb-image="1"` + `data-wb-image-index="<i>"`
 *     (+ optional `data-wb-image-field` if the array key isn't "images")
 */
export function useSectionSortable({ canvasRef, blocks, onUpdateBlockContent, enabled = true }) {
  const blocksRef = useRef(blocks);
  useEffect(() => { blocksRef.current = blocks; }, [blocks]);

  useEffect(() => {
    if (!enabled) return;
    let dragState = null;

    const findBlockId = (el) => {
      const wrapper = el.closest && el.closest('[data-testid^="live-block-"]');
      if (!wrapper) return null;
      return (wrapper.getAttribute('data-testid') || '').replace('live-block-', '');
    };

    const insideCanvas = (el) => {
      const canvas = canvasRef?.current;
      if (!canvas) return true; // not yet bound — accept; live-block check still scopes us
      return canvas.contains(el);
    };

    const classify = (target) => {
      if (!target || typeof target.closest !== 'function') return null;
      const btn = target.closest('[data-wb-button="1"]');
      if (btn && insideCanvas(btn)) {
        const idx = parseInt(btn.getAttribute('data-wb-button-index'), 10);
        if (Number.isNaN(idx)) return null;
        const field = btn.getAttribute('data-wb-button-field') || 'buttons';
        return { el: btn, idx, kind: 'buttons', field };
      }
      const img = target.closest('[data-wb-image="1"]');
      if (img && insideCanvas(img)) {
        const idx = parseInt(img.getAttribute('data-wb-image-index'), 10);
        if (Number.isNaN(idx)) return null;
        const field = img.getAttribute('data-wb-image-field') || 'images';
        return { el: img, idx, kind: 'image', field };
      }
      return null;
    };

    const clearHighlights = () => {
      document.querySelectorAll('.wb-sortable-active, .wb-sortable-target').forEach(el => {
        el.classList.remove('wb-sortable-active', 'wb-sortable-target');
        el.style.opacity = '';
        el.style.outline = '';
        el.style.outlineOffset = '';
      });
    };

    const onDragStart = (e) => {
      const c = classify(e.target);
      if (!c) return;
      const blockId = findBlockId(c.el);
      if (!blockId) return;
      dragState = { blockId, fromIdx: c.idx, kind: c.kind, field: c.field };
      try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', 'wb-sort'); } catch {}
      c.el.classList.add('wb-sortable-active');
      c.el.style.opacity = '0.35';
    };

    const onDragOver = (e) => {
      if (!dragState) return;
      const c = classify(e.target);
      if (!c || c.kind !== dragState.kind) return;
      const blockId = findBlockId(c.el);
      if (blockId !== dragState.blockId) return; // same section only
      e.preventDefault();
      try { e.dataTransfer.dropEffect = 'move'; } catch {}
      document.querySelectorAll('.wb-sortable-target').forEach(el => {
        if (el !== c.el) {
          el.classList.remove('wb-sortable-target');
          el.style.outline = ''; el.style.outlineOffset = '';
        }
      });
      c.el.classList.add('wb-sortable-target');
      c.el.style.outline = '2px dashed #8B5CF6';
      c.el.style.outlineOffset = '4px';
    };

    const onDrop = (e) => {
      if (!dragState) return clearHighlights();
      const c = classify(e.target);
      if (!c || c.kind !== dragState.kind) { clearHighlights(); dragState = null; return; }
      const blockId = findBlockId(c.el);
      if (blockId !== dragState.blockId) { clearHighlights(); dragState = null; return; }
      e.preventDefault();
      const toIdx = c.idx;
      const fromIdx = dragState.fromIdx;
      if (toIdx === fromIdx) { clearHighlights(); dragState = null; return; }
      const block = (blocksRef.current || []).find(b => b.id === blockId);
      if (!block) { clearHighlights(); dragState = null; return; }
      const content = block.content || {};
      const arr = Array.isArray(content[dragState.field]) ? [...content[dragState.field]] : null;
      if (!arr || fromIdx < 0 || fromIdx >= arr.length || toIdx < 0 || toIdx >= arr.length) {
        clearHighlights(); dragState = null; return;
      }
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      onUpdateBlockContent(blockId, { ...content, [dragState.field]: arr });
      clearHighlights();
      dragState = null;
    };

    const onDragEnd = () => { clearHighlights(); dragState = null; };

    document.addEventListener('dragstart', onDragStart, true);
    document.addEventListener('dragover', onDragOver, true);
    document.addEventListener('drop', onDrop, true);
    document.addEventListener('dragend', onDragEnd, true);

    return () => {
      document.removeEventListener('dragstart', onDragStart, true);
      document.removeEventListener('dragover', onDragOver, true);
      document.removeEventListener('drop', onDrop, true);
      document.removeEventListener('dragend', onDragEnd, true);
    };
  }, [canvasRef, enabled, onUpdateBlockContent]);
}
